<template>
    <HeaderContent title="Vehicle Maintenances"/>
    <ActionContent
        ctaText="Maintenance"
        :filterDate="false"
        :filterModal="false"
        @action="router.push('/vehicle-maintenance/add')"
        @search-query="searchData"
    />
    <DataTable
        :data="maintenanceList"
        :header="headers"
        :custom-actions="customActions"
    >
        <template #cell(payment_status)="{ item }">
             <span
                class="badge"
                :class="item.payment_status === 'Paid' ? 'bg-success' : 'bg-warning'"
            >
                {{ item.payment_status }}
            </span>
        </template>
        <template #cell(status)="{ item }">
             <span
                class="badge"
                :class="item.status === 'In Progress' ? 'bg-warning' : 'bg-success'"
            >
                {{ item.status }}
            </span>
        </template>
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from '@/components/HeaderContent.vue';
import ActionContent from '@/components/ActionContent.vue';
import DataTable from '@/components/DataTable.vue';
import { ref, onMounted, inject } from 'vue';
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import moment from "moment";

const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");
const toast = useToast();
const router = useRouter();

const maintenanceList = ref([]);

const headers = ref([
    { key: "no", label: "No", sortable: true, class: "text-center" },
    { key: "service_date", label: "Service Date", sortable: true, class: "text-left" },
    { key: "vehicle_name", label: "Vehicle", sortable: true, class: "text-left" },
    { key: "staff.fullname", label: "Driver", sortable: true, class: "text-left" },
    { key: "total_km_service", label: "KM Service", sortable: true, class: "text-left" },
    { key: "total_km", label: "KM Return", sortable: true, class: "text-left" },
    { key: "grand_total_format", label: "Grand Total", sortable: true, class: "text-right" },
    { key: "payment_status", label: "Payment Status", sortable: false, class: "text-center" },
    { key: "status", label: "Maintenance Status", sortable: false, class: "text-center" },
    { key: "action", label: "Actions", class: "text-center" },
]);

const customActions = ref([
    {
        label: "Detail",
        variant: "primary",
        icon: "fas fa-info-circle",
        action: (item) => {
            router.push(`/vehicle-maintenance/detail/${item.uuid}`);
        },
    },
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
             router.push(`/vehicle-maintenance/edit/${item.uuid}`);
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this maintenance record?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const getAll = async (search = "") => {
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle-maintenance`, {
            params: {
                search: search,
            },
            headers: {
                "Content-type": "application/json",
                Authorization: "Bearer " + auth._token,
            },
        });

        const res = response.data;

        if (res.data) {
            maintenanceList.value = res.data.map((item) => ({
                ...item,
                total_km: item.total_km || "-",
                vehicle_name: item.vehicle?.name + " (" + item.vehicle?.plate_number + ")",
                service_date: formatDate(item.service_date),
                grand_total_format: formatRupiah(item.grand_total),
            }));
        }
    } catch (error) {
        console.error("Failed to fetch data", error);
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/vehicle-maintenance/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;
             // Ensure response has status as expected or just rely on 200 OK
            if (response.status === 200 || res.status) {
                toast.success(res.message || 'Deleted successfully');
                getAll();
            }
        } catch (error) {
             toast.error(error.response?.data?.message || "Failed to delete record");
        } finally {
            loader.hide();
        }
    }
};

const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("DD MMM YYYY");
};

const formatRupiah = (number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(number);
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
